import { useState } from 'react';
import { ShieldCheck, Lock } from 'lucide-react';
import { setAiConsent } from '../utils/aiConsent';

export function AiConsentModal({ userId, onAccept, onDecline }) {
  const [saving, setSaving] = useState(false);

  const save = async (accepted) => {
    setSaving(true);
    try {
      await setAiConsent(userId, { accepted });
      accepted ? onAccept() : onDecline();
    } catch {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="AI data consent"
    >
      {/* Non-dismissable backdrop */}
      <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm" />

      <div className="relative bg-white rounded-[2rem] shadow-2xl w-full max-w-sm p-7 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">

        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="shrink-0 p-3 bg-stone-50 rounded-2xl border border-stone-100">
            <ShieldCheck size={22} className="text-stone-600" />
          </div>
          <div>
            <h2 className="font-bold text-stone-800 text-lg leading-snug">
              A note on AI features
            </h2>
            <p className="text-xs text-stone-400 mt-1">One-time notice · you can change this any time</p>
          </div>
        </div>

        {/* Body */}
        <div className="space-y-4 text-sm text-stone-600 leading-relaxed">
          <p>
            Mindwire uses an AI model to power <strong className="text-stone-800">chat</strong> and
            automatic <strong className="text-stone-800">tagging</strong> of your journal entries.
            To do this, relevant parts of your writing are sent to a model hosted on Hugging Face.
          </p>

          <div className="flex gap-3 p-4 bg-stone-50 rounded-2xl border border-stone-100">
            <Lock size={15} className="shrink-0 text-stone-400 mt-0.5" />
            <p className="text-xs text-stone-500 leading-relaxed">
              Your data is encrypted at rest using a key only you hold. It is sent to the
              AI model solely for processing your request and is never stored or used to train
              external models.
            </p>
          </div>

          <p>
            You can pause AI features at any time from
            <span className="font-medium text-stone-700"> Profile → Safety → AI Features</span>.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 pt-1">
          <button
            onClick={() => save(true)}
            disabled={saving}
            className="w-full py-3.5 bg-stone-800 text-white rounded-xl font-semibold hover:bg-black transition-all disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'I agree — enable AI features'}
          </button>
          <button
            onClick={() => save(false)}
            disabled={saving}
            className="w-full py-3 bg-stone-100 text-stone-600 rounded-xl font-semibold hover:bg-stone-200 transition-all disabled:opacity-40"
          >
            No thanks
          </button>
        </div>
      </div>
    </div>
  );
}
